import { type FC, useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Box, Column, Columns, TextLink } from 'braid-design-system';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';
import {
  PAGE_QUERY_PARAM_KEY,
  PAGE_TAB_PARAM_KEY,
} from 'src/pages/CandidateManagementPage/queryParams';
import JobSection from 'src/components/app/JobSection';
import UnhandledErrorPageBlock from '../../components/app/UnhandledErrorPageBlock';
import * as pageBlockStyles from '../../components/layouts/PageBlock/PageBlock.css';
import { UserLoadState } from '../../stores/UserStore/types';
import { getSignInUrl } from '../../pages/CandidateManagementPage/utils/getSignInUrl';
import PageAlert from './PageAlert';
import { useStores } from 'src/utils/hooks/useStores';
import CandidateList from 'src/components/list/index';
import { MobileFilterSection } from '../../components/app/MobileFilterSection';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import { IntercomConfiguration } from 'src/modules/intercom/IntercomConfiguration';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { isANZSite } from 'src/utils/site';
import { UserActionStatusProvider } from 'src/components/common/UserActionStatusContext';
import {
  getAnzCsPhoneNumberFromSite,
  getHelpCentreUrlFromSite,
} from '../../utils/getCsContactMethodMessageFromSite';
import { BillableApplicantsDialog } from '../CandidateManagementPage/BillableApplicantsDialog/BillableApplicantDialog';
import {
  trackCandidateCardStatus,
  trackClickBillableApplicantsDefinition,
  trackFreemiumButtonDisplay,
  trackFreemiumButtonPressedEvent,
  trackSelectAllProspectsClicked,
  trackSortOptionChangedEvent,
} from 'src/utils/events';
import { HOTJAR_TAG_SORT } from 'src/constants/hotjar';
import { sendHotjarTag } from 'src/utils/hotjar';
import { SUPPORT_BACK_BUTTON } from 'src/utils/buildHandleEnterAndSpaceKeyPress';
import { type PageTab, PageTabs } from '../CandidateManagementPage/PageTabs';
import loadable from 'sku/@loadable/component';
import { AppPageLayout } from './AppPageLayout';
import { useFilters } from 'src/components/app/FilterSideBar/useFilters';
import { MobileStickyHeader } from './MobileStickyHeader';
import { FreemiumButton } from './FreemiumButton';
import { useJobDetailsQuery } from './AppPageBlock.hirer.generated';
import useQueryParamsStore from 'src/utils/hooks/useQueryParamsStore';
import ActionToolbar from 'src/components/list/ActionToolbar';
import { FilterTabletDesktopContentLayout } from './FilterTabletDesktopContentLayout';
import { FilterDrawerTablet } from './FilterDrawerTablet';
import { FilterDesktop } from './FilterDesktop';
import AppErrorBoundary from 'src/components/app/AppErrorBoundary';
import useHandleStatusChange from '../hooks/useHandleStatusChange';
import Filters from 'src/components/common/Filters/Filters';
import { FilterDrawerMobile } from './FilterDrawerMobile';

const HirerJobActivity = loadable(
  () => import('src/components/app/HirerActivity/HirerJobActivity'),
);

const isPageTab = (value: unknown): value is PageTab =>
  value === 'applications' || value === 'notes';

interface AppPageBlockProps {
  employerHeader: JSX.Element;
}

export const CandidateManagementApp: FC<AppPageBlockProps> = observer(
  ({ employerHeader }) => {
    const { t } = useTranslations(translations);
    const { userStore, jobStore, isError } = useStores();
    const { site, language } = useAppConfig();

    if (userStore.userLoadState === UserLoadState.UNAUTHENTICATED) {
      return (
        <PageBlock>
          <PageAlert>
            {t('Sign in to see this page.', {
              Link: (children) => (
                <TextLink
                  key={UserLoadState.UNAUTHENTICATED}
                  href={getSignInUrl()}
                >
                  {children}
                </TextLink>
              ),
            })}
          </PageAlert>
        </PageBlock>
      );
    }

    if (userStore.userLoadState === UserLoadState.FORBIDDEN) {
      return (
        <PageBlock>
          <PageAlert>
            {t(
              "This account can't access this page. Would you like to switch accounts?",
              {
                Link: (children) => (
                  <TextLink key={UserLoadState.FORBIDDEN} href={getSignInUrl()}>
                    {children}
                  </TextLink>
                ),
              },
            )}
          </PageAlert>
        </PageBlock>
      );
    }

    if (!jobStore.hasAccess) {
      const csPhoneNumber = getAnzCsPhoneNumberFromSite(site);

      const noAccessToJobMessage = isANZSite(site)
        ? t(
            'If you think you should be one of them, ask your system administrator...',
            {
              TelLink: () => (
                <TextLink href={`tel:${csPhoneNumber}`}>
                  {csPhoneNumber}
                </TextLink>
              ),
            },
          )
        : t(
            'If you think you should be one of them, ask your system administrator - with Customer Service link',
            {
              Link: (children) => (
                <TextLink href={getHelpCentreUrlFromSite(site, language)}>
                  {children}
                </TextLink>
              ),
            },
          );
      return (
        <PageBlock>
          <PageAlert>
            {t('Only some people can see this page')}
            <br />
            {noAccessToJobMessage}
          </PageAlert>
        </PageBlock>
      );
    }

    if (isError || userStore.userLoadState === UserLoadState.ERROR) {
      return (
        <PageBlock>
          <Box marginY="small" marginX="gutter">
            <UnhandledErrorPageBlock />
          </Box>
        </PageBlock>
      );
    }

    return <AppPageBlock employerHeader={employerHeader} />;
  },
);

const AppPageBlock: FC<AppPageBlockProps> = observer(({ employerHeader }) => {
  const store = useStores();
  const {
    seekAdvertiserId,
    jobApplicationStore,
    userStore,
    isMobile,
    uiStore,
    jobStore,
  } = store;
  const { questionnaire: questionStore, job } = jobStore;
  const { language, router } = useAppConfig();
  const [isFilterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const questions = jobApplicationStore.filtersStore.questions;

  const { filters, filterFns: useFiltersFns } = useFilters({
    language,
  });

  const [isAtTopOfPage, setIsAtTopOfPage] = useState(true);
  const [, setPageQueryString] = useQueryParam(
    PAGE_QUERY_PARAM_KEY,
    NumberParam,
  );
  const [selectedPageTabUntyped, setPageTabString] = useQueryParam(
    PAGE_TAB_PARAM_KEY,
    StringParam,
  );
  const [queryParams] = useQueryParamsStore();
  const [isStickyOnDesktop, setIsStickyOnDesktop] = useState(false);
  const [isBillableApplicantsDialogOpen, setIsBillableApplicantsDialogOpen] =
    useState<boolean>(false);

  useEffect(() => {
    store.jobApplicationStore.updateFromQuery({
      queryParams: {
        jobId: queryParams.jobId,
        positionId: queryParams.positionId,
        bucket: queryParams.bucket,
        page: queryParams.page,
        selectedProspectId: queryParams.selectedProspectId,
        search: queryParams.search,
        overlay: queryParams.overlay,
        referrer: queryParams.referrer,
        tab: queryParams.tab,
      },
      language,
      initialLoad: false,
      filtersEventCaptureData: {
        filtered_apps_in_bucket:
          filters.filtersEventCaptureData.filtered_apps_in_bucket,
        filters_applied_to_bucket:
          filters.filtersEventCaptureData.filters_applied_to_bucket,
        total_apps_in_bucket:
          filters.filtersEventCaptureData.total_apps_in_bucket,
      },
      filters: filters.searchFilters,
    });
  }, [
    queryParams.jobId,
    queryParams.positionId,
    queryParams.bucket,
    queryParams.page,
    queryParams.selectedProspectId,
    queryParams.search,
    queryParams.overlay,
    queryParams.referrer,
    queryParams.tab,
    store,
    language,
    filters.filtersEventCaptureData.filtered_apps_in_bucket,
    filters.filtersEventCaptureData.filters_applied_to_bucket,
    filters.filtersEventCaptureData.total_apps_in_bucket,
    filters.searchFilters,
  ]);

  const setSelectedPageTab = (tab: PageTab) => {
    switch (tab) {
      case 'notes':
        setPageTabString('notes');
        break;
      default:
        setPageTabString(undefined);
    }
  };
  const selectedPageTab = isPageTab(selectedPageTabUntyped)
    ? selectedPageTabUntyped
    : 'applications';

  const shouldShowRecommendedCandidates = job.isActive;

  const openBillableAlertFromBanner = () => {
    trackClickBillableApplicantsDefinition({
      actionOrigin: 'Candidate Listing - Billable Alert',
    });
    setIsBillableApplicantsDialogOpen(true);
  };

  const upgradeJobLink = job.jobInformation.jobId
    ? router.upgradeJob({ jobId: jobApplicationStore.jobId })
    : '';

  const { data } = useJobDetailsQuery({
    variables: {
      advertiserId: seekAdvertiserId,
      jobId: jobApplicationStore.jobId.toString(),
      language,
    },
  });

  const shouldShowFreemium =
    upgradeJobLink &&
    userStore.user.canCreateJobs &&
    data?.getJobDetails?.canBeUpgraded;

  const hidePageTabs =
    isMobile && (!shouldShowRecommendedCandidates || !isAtTopOfPage);

  const isMustHaveToggleSelected =
    jobApplicationStore.filtersStore.isMustHaveToggleSelected;

  const onSortByChange: (params: { sortBy: string }) => Promise<void> = async ({
    sortBy,
  }) => {
    const defaultPage = 1;

    trackSortOptionChangedEvent({ sortMode: sortBy });
    sendHotjarTag(HOTJAR_TAG_SORT);

    await jobApplicationStore.loadJobApplications({
      sortBy,
      page: defaultPage,
      language,
      filters: filters.searchFilters,
    });
    setPageQueryString(defaultPage, SUPPORT_BACK_BUTTON);
  };

  const { handleStatusChange } = useHandleStatusChange({
    filters: filters.searchFilters,
    filtersEventCaptureData: filters.filtersEventCaptureData,
  });

  const screeningQuestionFilters = (
    <Filters
      isMustHaveToggleSelected={isMustHaveToggleSelected}
      questions={questions}
      onMustHaveFilterToggled={useFiltersFns.onMustHaveFilterToggled}
      onParentFilterSelected={useFiltersFns.onParentFilterSelected}
      onFilterSelected={useFiltersFns.onFilterSelected}
    />
  );

  const shouldShowClearFilterButton = filters.selectedFilterCount > 0;
  const filterTabletDesktopContentLayout = (
    <FilterTabletDesktopContentLayout
      clearFilters={useFiltersFns.clearFilters}
      screeningQuestionFilters={screeningQuestionFilters}
    />
  );

  const renderPageContent = () => {
    if (selectedPageTab === 'applications') {
      return (
        <AppErrorBoundary>
          <Box
            data-cy="main-content"
            marginX={['none', 'xxsmall']}
            background="surface"
          >
            <Columns space={['none', 'none', 'small']}>
              <Column width="content">
                <FilterDesktop
                  shouldShowClearFilterButton={shouldShowClearFilterButton}
                  content={filterTabletDesktopContentLayout}
                  isStickyOnDesktop={isStickyOnDesktop}
                  resetFilters={useFiltersFns.resetFilters}
                />
              </Column>

              <Column>
                <CandidateList
                  actionToolbar={
                    <ActionToolbar
                      jobApplicationStore={jobApplicationStore}
                      updateStatusForSelectedApplications={(bucket) =>
                        jobApplicationStore.updateStatusForSelectedApplications(
                          {
                            bucket,
                            store: jobApplicationStore.rootStore,
                            reloadJobApplications: async () => {
                              await jobApplicationStore.loadJobApplications({
                                language,
                                filters: filters.searchFilters,
                              });
                            },
                            filtersEventCaptureData:
                              filters.filtersEventCaptureData,
                          },
                        )
                      }
                      sortBy={jobApplicationStore.sortBy}
                      hasSearchText={jobApplicationStore.hasSearchText}
                      hasQuestion={Boolean(jobStore.questionnaire?.hasQuestion)}
                      onSortByChange={onSortByChange}
                      isMobile={isMobile}
                      isJobTabsSticky={uiStore.isJobTabsSticky}
                      showOldJobIntercom={jobStore.job.showOldJobIntercom}
                      selectAllStatus={jobApplicationStore.selectAllStatus}
                      selectAll={(isCheckboxChecked) => {
                        if (isCheckboxChecked) {
                          jobApplicationStore.clickClearAllSelected({
                            tracking: {
                              actionOrigin:
                                'Candidate Listing - Bulk Select Check Box',
                            },
                          });
                        } else {
                          jobApplicationStore.clickSelectAllOnCurrentPage();
                        }
                      }}
                      onExpandApplicationCardState={() => {
                        trackCandidateCardStatus({
                          candidateCardStatus: 'Expand',
                          actionOrigin: 'Candidate Listing - Listing Change',
                        });
                      }}
                      onCollapseApplicationCardState={() => {
                        trackCandidateCardStatus({
                          candidateCardStatus: 'Collapse',
                          actionOrigin: 'Candidate Listing - Listing Change',
                        });
                      }}
                      selectedFilterCount={filters.selectedFilterCount}
                      openFilterDrawer={() => setFilterDrawerOpen(true)}
                      addCandidate={async (candidateFormValues) => {
                        await jobApplicationStore.addCandidate(
                          candidateFormValues,
                          language,
                          async () => {
                            jobApplicationStore.loadJobApplications({
                              language,
                              filters: filters.searchFilters,
                            });
                          },
                        );
                      }}
                      selectAllJobApplicationsInBucket={({ onCompleted }) => {
                        jobApplicationStore.loadAllJobApplications({
                          selectAll: true,
                          onCompleted: () => {
                            trackSelectAllProspectsClicked({
                              objectInteraction:
                                jobApplicationStore.selectAllStatus,
                              bulkActionCount:
                                jobApplicationStore.selectedCount,
                            });
                            onCompleted();
                          },
                          language,
                          filters: filters.searchFilters,
                        });
                      }}
                    />
                  }
                  reloadJobApplicationsWithLastResultSearchText={() => {
                    jobApplicationStore.loadJobApplications({
                      searchText: jobApplicationStore.lastResultSearchText,
                      language,
                      filters: filters.searchFilters,
                    });
                  }}
                  clearTextAndReloadJobApplications={() => {
                    jobApplicationStore.updateSearchText('');
                    // FIXME: This is not updating the query param for search term, which is a bug
                    jobApplicationStore.loadJobApplications({
                      language,
                      searchText: jobApplicationStore.searchText,
                      filters: filters.searchFilters,
                    });
                  }}
                  isStickyOnDesktop={isStickyOnDesktop}
                  openBillableDialog={openBillableAlertFromBanner}
                  selectedFilterCount={filters.selectedFilterCount}
                  useFiltersFns={useFiltersFns}
                  isMustHaveToggleSelected={isMustHaveToggleSelected}
                  questions={questions}
                  filtersEventCaptureData={filters.filtersEventCaptureData}
                  handleStatusChange={handleStatusChange}
                />
              </Column>
            </Columns>
          </Box>
        </AppErrorBoundary>
      );
    }
    if (selectedPageTab === 'notes') {
      return <HirerJobActivity handleStatusChange={handleStatusChange} />;
    }
  };

  const pageTabs = (
    <PageTabs
      isStickyOnDesktop={isStickyOnDesktop}
      selectedPageTab={selectedPageTab}
      setIsStickyOnDesktop={setIsStickyOnDesktop}
      setSelectedPageTab={setSelectedPageTab}
      showRecommendedCandidates={shouldShowRecommendedCandidates}
      jobId={jobApplicationStore.jobId}
      onApplicationsTabClick={() =>
        jobApplicationStore.loadJobApplications({
          language,
          filters: filters.searchFilters,
        })
      }
    />
  );

  const mobileFilterSection = (
    <MobileFilterSection
      sortBy={jobApplicationStore.sortBy}
      hasSearchText={jobApplicationStore.hasSearchText}
      hasQuestion={Boolean(jobStore.questionnaire?.hasQuestion)}
      onSortByChange={onSortByChange}
      selectedFilterCount={filters.selectedFilterCount}
      openFilterDrawer={() => setFilterDrawerOpen(true)}
    />
  );

  return (
    <UserActionStatusProvider
      jobId={`${jobApplicationStore.jobId}`}
      userId={store.userStore.user.userId || 0}
    >
      <IntercomConfiguration />
      <AppPageLayout
        mobileStickyHeader={
          isMobile && (
            <MobileStickyHeader
              isAtTopOfPage={isAtTopOfPage}
              setIsAtTopOfPage={setIsAtTopOfPage}
              employerHeader={employerHeader}
              pageTabs={shouldShowRecommendedCandidates && pageTabs}
              mobileFilterSection={mobileFilterSection}
            />
          )
        }
        jobSection={{
          boxShadow: hidePageTabs ? 'small' : undefined,
          element: (
            <JobSection
              job={jobStore.job}
              isRecommendedCandidatesTabShown={shouldShowRecommendedCandidates}
              openBillableDialog={() => {
                trackClickBillableApplicantsDefinition({
                  actionOrigin:
                    'Candidate Listing - Billable Applicants Button',
                });
                setIsBillableApplicantsDialogOpen(true);
              }}
              freemiumButton={
                shouldShowFreemium ? (
                  <FreemiumButton
                    link={upgradeJobLink}
                    onFreemiumButtonClicked={() =>
                      trackFreemiumButtonPressedEvent()
                    }
                    onFreemiumButtonLoaded={() => {
                      trackFreemiumButtonDisplay({
                        hasJobUpgradeButton: true,
                      });
                    }}
                  />
                ) : null
              }
            />
          ),
        }}
        pageTabs={!hidePageTabs && pageTabs}
        mobileFilterSection={mobileFilterSection}
        content={renderPageContent()}
        billableApplicantsDialog={
          job.jobInformation.billableApplicants && (
            <BillableApplicantsDialog
              hasBillableApplicantsCountError={
                !job.jobInformation.billableApplicants.success
              }
              isOpen={isBillableApplicantsDialogOpen}
              mandatoryQuestions={questionStore.getMandatoryQuestions()}
              onClose={() => {
                setIsBillableApplicantsDialogOpen(false);
              }}
            />
          )
        }
      />
      {isMobile ? (
        <FilterDrawerMobile
          showDrawer={isFilterDrawerOpen}
          setShowDrawer={setFilterDrawerOpen}
          screeningQuestionFilters={screeningQuestionFilters}
          shouldShowClearFilterButton={shouldShowClearFilterButton}
          useFiltersFns={useFiltersFns}
        />
      ) : (
        <FilterDrawerTablet
          isOpen={isFilterDrawerOpen}
          close={() => setFilterDrawerOpen(false)}
          content={filterTabletDesktopContentLayout}
          resetFilters={useFiltersFns.resetFilters}
          shouldShowClearFilterButton={shouldShowClearFilterButton}
        />
      )}
    </UserActionStatusProvider>
  );
});

const PageBlock: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <Box
    className={pageBlockStyles.pageBlock}
    display="flex"
    flexDirection="column"
    paddingY="none"
    paddingX={['none', 'xsmall']}
    marginY="none"
  >
    {children}
  </Box>
);
